import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import universal from '@core-ui/universal';
import { DropdownProps } from '@core-ui/dropdown';
import { useLocalize } from '@core-ui/i18n';
import { useAppState } from '@core-ui/app-state-provider';
import { BreakpointContext, XLARGE } from '@core-ui/breakpoint-provider';
import { FeatureFlagsContext } from '@core-ui/feature-flags';
import { Direction, Field, SortByProps, SortByValue } from '../../types';
import {
  getAria,
  getControlStyles,
  getMenuStyles,
  getOptionStyles,
  getSelectStyles,
  getValueByFieldAndDirection,
} from '../../collaborators';

const Dropdown = universal<DropdownProps>(import('@core-ui/dropdown'), {
  key: 'Dropdown',
});

const SortBy: FC<SortByProps> = (props: SortByProps): JSX.Element => {
  const {
    value: defaultValue,
    onChange,
    hasSeparateLabel = false,
    isSortByRatingsEnabled = false,
    isBRRedesignEnabled = false,
    isBRRedesignOpacityEnabled = false,
  } = props;

  const { brandName } = useAppState();
  const isBR = brandName === 'br' || brandName === 'brfs';

  const { localize } = useLocalize();
  const { smallerThan } = useContext(BreakpointContext);
  const { enabledFeatures } = useContext(FeatureFlagsContext);
  const [aria, setAria] = useState<string>();
  const [value, setValue] = useState<string>();
  const usePlaceholderOption = !hasSeparateLabel;
  const native = smallerThan(XLARGE);
  const localizeKey = native ? 'mobile' : 'desktop';
  const placeholder = isBRRedesignEnabled
    ? localize('sortBy.mobile.placeholder.caps')
    : localize(`sortBy.${localizeKey}.placeholder`);
  const selectProps = { id: 'sortBySelect' };
  const capitalizedOption = isBRRedesignEnabled ? '.caps' : '';

  const values = useMemo(
    () => {
      const sortByValues: SortByValue[] = [
        {
          field: Field.Price,
          label: localize(
            `sortBy.${localizeKey}.price.options.featured${capitalizedOption}`
          ),
          direction: Direction.Unset,
        },
        {
          field: Field.Price,
          label: localize(
            `sortBy.${localizeKey}.price.options.asc${capitalizedOption}`
          ),
          direction: Direction.Ascending,
        },
        {
          field: Field.Price,
          label: localize(
            `sortBy.${localizeKey}.price.options.desc${capitalizedOption}`
          ),
          direction: Direction.Descending,
        },
      ];

      const sortByValuesWithTopRated: SortByValue[] = [
        ...sortByValues,
        {
          field: Field.ReviewScore,
          label: localize(`sortBy.${localizeKey}.reviewScore.options.desc`),
          direction: Direction.Descending,
        },
      ];

      return isSortByRatingsEnabled ? sortByValuesWithTopRated : sortByValues;
    },
    [localize, localizeKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const options = values.map(({ label }) => label);

  const handleChange = useCallback(
    (option: ReactNode): void => {
      const selected = values.find(
        ({ label }: SortByValue) => label === option
      );

      if (selected === undefined) return;

      const { label } = selected;

      setAria(getAria(placeholder, label));
      setValue(label);

      onChange?.(selected);
    },
    [onChange, placeholder, values]
  );

  useEffect(() => {
    const {
      direction: defaultDirection,
      field: defaultField,
      label: defaultLabel,
    } = defaultValue ?? {};

    if (defaultLabel) {
      setAria(getAria(placeholder, defaultLabel));

      return setValue(defaultLabel);
    }

    const value = getValueByFieldAndDirection({
      defaultDirection,
      defaultField,
      values,
    });

    if (value === undefined) return value;

    const { label } = value;

    setAria(getAria(placeholder, label));

    return setValue(label);
  }, [defaultValue, placeholder, values]);

  return (
    <Dropdown
      ariaDescribedByText={aria}
      enabledFeatures={enabledFeatures}
      label={placeholder}
      native={native}
      onChange={handleChange}
      options={options}
      placeholder={hasSeparateLabel ? '' : placeholder}
      selectProps={selectProps}
      styles={{
        Control: getControlStyles(
          isBR,
          isBRRedesignEnabled,
          isBRRedesignOpacityEnabled
        ),
        Menu: getMenuStyles(isBRRedesignEnabled),
        Option: getOptionStyles(isBR, isBRRedesignEnabled),
        Select: getSelectStyles(hasSeparateLabel, isBR, isBRRedesignEnabled),
      }}
      // @ts-ignore
      usePlaceholderOption={usePlaceholderOption}
      value={value}
    />
  );
};

export default SortBy;
